import React from 'react';
import CombatTooltip from '../GameTip/CombatTooltip';
import { HeartRatePill } from '../StatusTags/KcStatusPills';
import { isHtnDrugTooltipRowsEnabled } from '../../utils/envFlags';
import {
  htnDrugInfluenceGroups,
  fedByDrugNames,
  pulseShiftRows,
  pulseNetDirectionWord,
} from './HtnDrugInfluenceTooltip';
import { ShiftArrow } from './PlayerPotassiumTag';
import { DamageSword } from './ResultPillIcons';
import { isCombatTerminalPhase } from './playerbarPhase';

// HTN Regimen pulse tag (2026-07-09). The antihypertensive loadout shifts the
// player's heart rate: DHP CCBs cause reflex tachycardia (+1 each), non-DHP CCBs
// and beta-blockers slow the pulse (-1 each). That net shift is added straight
// into the round's question count (compute_batch_size: base 3, floored at 2), so
// a racing pulse means MORE questions per round and a slowed pulse means fewer.
// Until now nothing surfaced it, so the player could not tell why a round grew or
// shrank. This tag renders whenever the pulse is shifted at all (net != 0), with
// the tooltip naming whether that shift speeds or slows the round. Distinct from
// the Levodopa PlayerPulseTag, which is a different mechanic in Bradyx fights.
//
// NOTE: it is intentionally gated on net != 0, NOT on the brady/tachy state
// threshold (+/-2). The batch-size effect fires at any nonzero net, so a single
// DHP (+1, "steady" clinically) still adds a question and must be explained.

const heartbeatIcon = (size, className) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Heartbeat — heart silhouette with an ECG trace through the lower left. */}
    <path d="M19.5 13.572l-7.5 7.428l-2.896 -2.868m-6.117 -8.104a5 5 0 0 1 9.013 -3.022a5 5 0 1 1 7.5 6.572" />
    <path d="M3 13h2l2 3l2 -6l1 3h3" />
  </svg>
);

// The countdown clock. Same face the Latanz delayed-damage tag, GameTip's ClockIcon,
// the `round`/`rounds` status word and the drug toast's qualifier all draw, because
// every one of them means the same thing: this lands later. Drawn inline per this
// directory's idiom (each Player*Tag owns its own glyphs) rather than imported.
const pendingClockIcon = (size, className) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v5l3.2 2.2" />
  </svg>
);

/**
 * THE pending-pulse selector. Reads the server's `htn_regimen_state.timed_effects`
 * and returns the pulse mechanic that is drafted but has not activated yet, or null.
 *
 * One selector so every surface agrees on WHICH effect is arming and how long is
 * left. The server owns both facts (`state` and `rounds_until_activation`); nothing
 * here recomputes them from the loadout, because a client that recomputes timing is
 * a client that can disagree with the engine about what round it is.
 */
export function selectPendingPulseEffect(timedEffects) {
  if (!Array.isArray(timedEffects)) return null;
  return timedEffects.find((effect) => (
    effect
    && effect.state === 'pending'
    && String(effect.id || '') === 'dhp_reflex_tachycardia'
    && Number(effect.rounds_until_activation || 0) > 0
  )) || null;
}

function pendingRoundsOf(pending) {
  const rounds = Number(pending?.rounds_until_activation);
  return Number.isFinite(rounds) ? Math.max(0, Math.trunc(rounds)) : 0;
}

// The state word for a net shift. Exported so the trigger pill's aria-label and the
// tooltip's own header can never drift apart.
export const HTN_AV_BLOCK_STATE = 'av_block';
// Mirrors HTN_AV_BLOCK_BEATS / HTN_AV_BLOCK_PER_BEAT in htn_regimen_axes.py. Used
// ONLY by the faithfulness fallback below, for a payload that names the state but
// predates the beats/per_beat fields. Without these the fallback reproduces the
// UNBLOCKED table, so such a payload would render 2 x 11 under an "AV Block"
// header - the one path left where the panel could contradict its own state word.
const AV_BLOCK_FALLBACK_BEATS = 1;
const AV_BLOCK_FALLBACK_PER_BEAT = 10;

export function htnPulseStateLabel(net, state = 'steady') {
  const n = Number.isFinite(Number(net)) ? Math.trunc(Number(net)) : 0;
  // AV block is tested FIRST and ignores the sign, because it is the one state
  // whose name is not a description of the net shift. The pulse really is slow,
  // but "Bradycardia" is not what the player needs to read: the conduction is
  // blocked, and that is a different fight.
  if (state === HTN_AV_BLOCK_STATE) return 'AV Block';
  return n > 0
    ? (state === 'tachy' ? 'Tachycardia' : 'Elevated Pulse')
    : (state === 'brady' ? 'Bradycardia' : 'Slowed Pulse');
}

/**
 * THE pulse tooltip. Returns CombatTooltip props (everything except children), so
 * every surface that hovers a heart-rate pill renders the identical panel from this
 * one source instead of reconstructing it.
 *
 * Edward 2026-07-25: the drug TOAST's embedded HR tooltip had drifted into a
 * reconstruction (in fact it had no tooltip at all, while the combat rail hovered
 * this rich panel). The authoritative truth for these embedded tooltips is how the
 * status reads on hover in the combat status rail, so the rail's panel is the single
 * definition and the toast mounts it around its own trigger pill.
 */
export function htnPulseTooltipProps({
  net = 0,
  state = 'steady',
  perBeatDispense,
  beats,
  activeEquippedEffects = [],
  pending = null,
  avBlockWarning = '',
}) {
  const n = Number.isFinite(Number(net)) ? Math.trunc(Number(net)) : 0;
  const isUp = n > 0;
  // AV BLOCK (2026-07-28). A beta blocker plus a non-DHP CCB collapses the round
  // to a single beat. Every line below that talks about the 2-question floor
  // becomes a lie in that state, so the panel branches on it explicitly instead
  // of letting the brady copy fall through. The server owns the state word; this
  // never re-derives the block from the loadout.
  const isBlocked = state === HTN_AV_BLOCK_STATE;
  // A drafted-but-not-yet-active mechanic (2026-07-28). DHP CCBs no longer speed the
  // pulse on contact: the reflex tachycardia arms and lands a round later. Until it
  // does, `net` genuinely excludes it, so the panel must say what is coming rather
  // than describe a shift the engine is not yet applying.
  const pendingRounds = pendingRoundsOf(pending);
  const pendingLabel = String(pending?.label || 'Compensatory Pulse');
  const pendingRoundWord = pendingRounds === 1 ? 'round' : 'rounds';
  // Per-strike DISPENSE (Edward 2026-07-25): the pulse also sets HOW your strikes
  // land — tachy throws MORE questions but each correct answer dispenses LESS damage
  // (per-beat 7 -> 5 -> 3); brady throws FEWER but each hits HARDER (11). The engine
  // is the single source (htn_regimen_axes.htn_per_beat_damage / htn_beats_for_pulse),
  // shipped on the pulse payload as per_beat_dispense + beats. We render those
  // verbatim. FAITHFULNESS CONTRACT: the local fallback below reproduces that exact
  // table only for older payloads that predate the fields — it must never diverge.
  const per = Number.isFinite(Number(perBeatDispense)) && Number(perBeatDispense) > 0
    ? Math.trunc(Number(perBeatDispense))
    : (isBlocked ? AV_BLOCK_FALLBACK_PER_BEAT : (n <= -1 ? 11 : n === 0 ? 7 : n === 1 ? 5 : 3));
  const strikes = Number.isFinite(Number(beats)) && Number(beats) > 0
    ? Math.trunc(Number(beats))
    : (isBlocked ? AV_BLOCK_FALLBACK_BEATS : Math.max(2, 3 + n));
  const strikeWord = strikes === 1 ? 'strike' : 'strikes';
  const dispenseWord = isUp ? 'each weaker' : 'each harder';
  const dispenseLine = isBlocked
    ? `One dispense per round: ${strikes} ${strikeWord} x ${per} damage.`
    : isUp
      ? `Each dispense hits for less: ${strikes} strikes x ${per} damage.`
      : `Fewer, harder dispenses: ${strikes} strikes x ${per} damage.`;
  const sign = isUp ? `+${n}` : `${n}`;
  const label = htnPulseStateLabel(n, state);

  // The down side is deliberately floor-aware and NON-numeric, not a symmetric
  // "-N questions". The engine floors the round at max(2, 3 + net), so ANY brady
  // (net <= -1) lands the round on the 2-question floor. A deeper pulse (net <= -2)
  // removes NO additional questions -> it is bradycardia margin, not a bigger cut.
  // Do NOT "symmetrize" this to "-N fewer questions"; that would lie. The up side
  // has no ceiling, so there +N is exactly +N questions.
  const lead = isBlocked
    ? 'Beta blockade plus a non-DHP CCB has blocked AV conduction.'
    : isUp
      ? 'Reflex tachycardia from DHP CCBs is speeding your pulse.'
      : 'Rate-slowing agents (beta-blockers, non-DHP CCBs) are slowing your pulse.';
  const detail = isBlocked
    ? `Rounds collapse to ${strikes} question${strikes === 1 ? '' : 's'} while both agents are equipped.`
    : isUp
      ? `Each round throws +${n} more question${n === 1 ? '' : 's'} to answer.`
      : (n <= -2
          ? 'Rounds are already at the 2-question floor. Deeper slowing is bradycardia margin, not fewer questions.'
          : 'This round drops to the 2-question floor.');
  // Pending framing. When the ONLY pulse mechanic in the loadout is still arming,
  // `net` is 0 and every line above describes a shift that is not happening, so the
  // panel switches to the countdown entirely. When something else is already shifting
  // the pulse (a beta-blocker alongside a DHP), the live rows stay and the countdown
  // is appended: what is happening now, then what is coming.
  const pendingLine = pendingRounds > 0
    ? `${pendingLabel} arms in ${pendingRounds} ${pendingRoundWord}.`
    : '';
  const isPendingOnly = pendingRounds > 0 && n === 0;
  const pendingColor = 'rgb(45, 212, 191)';
  // The draft-time warning. Only meaningful BEFORE the pair exists: once the block
  // is live every row above is already describing it, so repeating the caution
  // would be noise.
  const warnLine = !isBlocked && avBlockWarning ? String(avBlockWarning) : '';

  if (!isHtnDrugTooltipRowsEnabled()) {
    if (isPendingOnly) {
      return {
        inline: true,
        color: pendingColor,
        name: pendingLabel,
        placement: 'above-left',
        icon: pendingClockIcon(18, 'combat-tt__node-icon'),
        sections: [{
          lines: [
            'Reflex tachycardia from DHP CCBs has not started yet.',
            pendingLine,
            `This round runs at your un-shifted pulse: ${strikes} strikes x ${per} damage.`,
          ],
        }],
      };
    }
    return {
      inline: true,
      // The house derangement red (every playerbar tag's bad state uses it) so a
      // blocked run does not read as merely "very bradycardic" in calm cyan.
      color: (isUp || isBlocked) ? 'rgb(248, 113, 113)' : 'rgb(56, 189, 248)',
      name: label,
      placement: 'above-left',
      icon: heartbeatIcon(18, 'combat-tt__node-icon'),
      sections: [{ lines: [lead, detail, dispenseLine, pendingLine, warnLine].filter(Boolean) }],
    };
  }

  // Drug-agnostic status tooltip in the potassium-grade STAT grammar (Edward's
  // 2026-07-24 port-up to match the Claude Design "Player status tooltips" page).
  // Compact, full-width: the HR chip row carries the round effect on the RIGHT —
  // tachy "Each round +N question(s)" (red), brady "Rounds hold at 2 question floor"
  // (cyan, floor-aware + NON-numeric) — then a "Shifted by" net word + per-drug
  // direction ledger (DHP CCBs up/red, beta-blockers + non-DHP CCBs down/cyan). The
  // split header states the state (Heart rate │ Tachycardia).
  const pulseGroups = htnDrugInfluenceGroups(activeEquippedEffects, 'pulse');
  const shiftRows = pulseShiftRows(pulseGroups);
  const netWord = pulseNetDirectionWord(n);
  const netToneClass = isUp ? 'up' : 'down';
  // The countdown, in the same three-part statrow grammar the question-count row
  // uses, so "Arms in 1 round" reads with identical weight to "Each round +1
  // question". Teal numeral: the delay owns the colour, not the mechanic behind it.
  const pendingCountdownNode = (leadWord) => (
    <span className="gt-statrow">
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">{leadWord}</span></span>
      <span className="gt-stat gt-stat--qnum is-pending">
        {pendingClockIcon(12, 'gt-stat__icon')}
        <span className="gt-stat__v">{pendingRounds}</span>
      </span>
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">{pendingRoundWord}</span></span>
    </span>
  );
  // The blocked row states the collapsed count from the payload rather than a
  // literal 1, so it can never disagree with the round the engine actually serves.
  const blockedEffectNode = (
    <span className="gt-statrow">
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">Rounds collapse to</span></span>
      <span className="gt-stat gt-stat--qnum is-up"><span className="gt-stat__v">{strikes}</span></span>
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">{strikes === 1 ? 'question' : 'questions'}</span></span>
    </span>
  );
  const effectNode = isPendingOnly ? pendingCountdownNode('Arms in') : (isBlocked ? blockedEffectNode : (isUp ? (
    <span className="gt-statrow">
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">Each round</span></span>
      <span className="gt-stat gt-stat--qnum is-up"><span className="gt-stat__v">{`+${n}`}</span></span>
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">{n === 1 ? 'question' : 'questions'}</span></span>
    </span>
  ) : (
    <span className="gt-statrow">
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">Rounds hold at</span></span>
      <span className="gt-stat gt-stat--qnum is-down"><span className="gt-stat__v">2</span></span>
      <span className="gt-stat gt-stat--join"><span className="gt-stat__v">question floor</span></span>
    </span>
  )));
  const pulseBody = (
    <span className="gt-inflicts-rows gt-hr-rows">
      <span className="gt-inflicts">
        <span className="gt-inflicts__l">
          <HeartRatePill
            label={isPendingOnly ? 'HR' : `HR ${sign}`}
            className={isPendingOnly ? 'is-pending' : (isBlocked ? 'is-av-block' : (isUp ? 'is-tachy' : 'is-brady'))}
          />
        </span>
        <span className="gt-inflicts__r">{effectNode}</span>
      </span>
      {/* Per-strike DISPENSE row (Edward 2026-07-25). Chip on the LEFT, and on the
          RIGHT the CANONICAL damage indicator — the same ⚔ + red numeral primitive
          (.gt-next-dmg__sword + .gt-next-dmg, from decode.jsx's Deals row) that every
          enemy tooltip puts bottom-right, reused verbatim rather than re-invented.
          Edward 2026-07-25 (2nd pass): the chip alone on the left, the damage number
          ALONE on the right — no strikes×per formula, no clinical word. The strike
          count is already stated by the question-count row directly above, so the
          per-strike damage is the new information this row carries. The row keeps the
          .gt-inflicts--deals class, so it inherits the enemy Deals-row styling (red
          0.98rem/800 tabular numeral, 3px sword gap) with NO new CSS. */}
      <span className="gt-inflicts gt-inflicts--deals">
        <span className="gt-inflicts__l">
          <span className="gt-dispense-chip"><span className="gt-dispense-chip__label">Dispense</span></span>
        </span>
        <span className="gt-inflicts__r">
          <DamageSword className="gt-stat__icon gt-next-dmg__sword" />
          <span className="gt-next-dmg">{per}</span>
        </span>
      </span>
      {warnLine ? (
        <span className="gt-inflicts gt-inflicts--avblock">
          <span className="gt-inflicts__l"><span className="gt-text">AV block</span></span>
          <span className="gt-inflicts__r"><span className="gt-text">{warnLine}</span></span>
        </span>
      ) : null}
      {/* The countdown as its own row, for a loadout that is ALREADY shifting the
          pulse while a second mechanic arms behind it (a beta-blocker alongside a
          DHP CCB). Pending-only loadouts carry it in the HR row above instead, so it
          is never stated twice. */}
      {pendingRounds > 0 && !isPendingOnly && (
        <span className="gt-inflicts gt-inflicts--pending">
          <span className="gt-inflicts__l"><span className="gt-text">{pendingLabel}</span></span>
          <span className="gt-inflicts__r">{pendingCountdownNode('arms in')}</span>
        </span>
      )}
      {shiftRows.length > 0 && (
        <>
          <span className="gt-inflicts gt-inflicts--shift">
            {/* "Shifted by" is past tense and would lie about a loadout whose only
                pulse drug has not fired yet. The ledger still belongs here: it is
                what answers "which card is doing this". */}
            <span className="gt-inflicts__l">
              <span className="gt-text">{isPendingOnly ? 'Arming from' : 'Shifted by'}</span>
            </span>
            <span className="gt-inflicts__r">
              {isPendingOnly ? null : (
                <span className={`gt-shift-net gt-shift-net--${netToneClass}`}>
                  <span className="gt-shift-net__k">net</span>
                  <span className="gt-shift-net__v">{netWord}</span>
                </span>
              )}
            </span>
          </span>
          <span className="gt-shift-ledger">
            {shiftRows.map((rowItem) => (
              <span className="gt-shift-row" key={rowItem.slug}>
                <span className={`gt-shift-dir gt-shift-dir--${rowItem.direction}`}>
                  <ShiftArrow direction={rowItem.direction} />
                </span>
                <span className="gt-shift-name">{rowItem.drugName}</span>
                {rowItem.classLabel ? (
                  <span className="gt-shift-class">{rowItem.classLabel}</span>
                ) : null}
              </span>
            ))}
          </span>
        </>
      )}
    </span>
  );
  const fedBy = fedByDrugNames(pulseGroups);
  const ariaLabel = [
    `Heart rate · ${isPendingOnly ? `${pendingLabel} pending` : label}`,
    isPendingOnly
      ? `${pendingLabel} arms in ${pendingRounds} ${pendingRoundWord}`
      : isBlocked
        ? `Rounds collapse to ${strikes} question${strikes === 1 ? '' : 's'}`
        : (isUp ? `Each round throws +${n} more question${n === 1 ? '' : 's'}` : 'Rounds hold at the 2-question floor'),
    `Dispense ${strikes} ${strikeWord} of ${per} damage each, ${dispenseWord}`,
    fedBy.length
      ? `${isPendingOnly ? 'Arming from' : 'Shifted by'} ${shiftRows
          .map((rowItem) => `${rowItem.drugName} ${rowItem.direction === 'up' ? 'speeds' : 'slows'} the pulse`)
          .join(', ')}${isPendingOnly ? '' : `. Net ${netWord}`}`
      : '',
    pendingRounds > 0 && !isPendingOnly ? `${pendingLabel} arms in ${pendingRounds} ${pendingRoundWord}` : '',
    warnLine ? `AV block: ${warnLine}` : '',
  ].filter(Boolean).join('. ');

  return {
    color: isPendingOnly ? pendingColor : ((isUp || isBlocked) ? 'rgb(248, 113, 113)' : 'rgb(56, 189, 248)'),
    name: 'Heart rate',
    subtitle: isPendingOnly ? `${pendingLabel} pending` : label,
    placement: 'above-left',
    icon: isPendingOnly
      ? pendingClockIcon(18, 'combat-tt__node-icon')
      : heartbeatIcon(18, 'combat-tt__node-icon'),
    sections: [{ content: pulseBody }],
    ariaLabel,
  };
}

function PlayerHtnPulseTag({
  net = 0,
  state = 'steady',
  perBeatDispense,
  beats,
  activeEquippedEffects = [],
  pending = null,
  avBlockWarning = '',
  visible = false,
  phase,
}) {
  const n = Number.isFinite(Number(net)) ? Math.trunc(Number(net)) : 0;
  const pendingRounds = pendingRoundsOf(pending);
  // The tag also renders while a pulse mechanic is ARMING, not only while one is
  // active (2026-07-28). Before the delayed-activation primitive the gate was
  // `n === 0 -> null` alone, which meant a lone DHP CCB showed nothing at all in
  // round 1 and then a tachycardia tag appeared out of nowhere in round 2. The
  // telegraph is the point of delaying the effect.
  if (!visible || isCombatTerminalPhase(phase) || (n === 0 && pendingRounds <= 0)) {
    return null;
  }
  const isUp = n > 0;
  const isBlocked = state === HTN_AV_BLOCK_STATE;
  const isPendingOnly = pendingRounds > 0 && n === 0;
  const sign = isUp ? `+${n}` : `${n}`;
  const label = htnPulseStateLabel(n, state);
  const pendingLabel = String(pending?.label || 'Compensatory Pulse');
  const pendingRoundWord = pendingRounds === 1 ? 'round' : 'rounds';
  // The countdown suffix rides the pill whenever something is arming, so a mixed
  // loadout shows its live shift AND its pending one without a second tag.
  const pendingSuffix = pendingRounds > 0 ? (
    <>
      {pendingClockIcon(11, '')}
      {pendingRounds}
    </>
  ) : null;
  // The rail pill drops the numeric "+N" (Edward 2026-07-25): the glyph now BEATS at
  // a net-mapped rate instead. The signed count still rides the aria-label (a11y) and
  // the hover tooltip carries the full per-drug detail. A pill whose only mechanic is
  // still pending does NOT beat — there is no rate to feel yet.
  const trigger = (
    <HeartRatePill
      label="HR"
      beat={isPendingOnly ? null : n}
      className={isPendingOnly ? 'is-pending' : (isBlocked ? 'is-av-block' : (isUp ? 'is-tachy' : 'is-brady'))}
      suffix={pendingSuffix}
      aria-label={isPendingOnly
        ? `${pendingLabel} arms in ${pendingRounds} ${pendingRoundWord}`
        : [
          isBlocked
            ? `${label}, far fewer questions per round`
            : isUp ? `${label}, ${sign} questions per round` : `${label}, fewer questions per round`,
          pendingRounds > 0 ? `${pendingLabel} arms in ${pendingRounds} ${pendingRoundWord}` : '',
        ].filter(Boolean).join('. ')}
    />
  );
  return (
    <CombatTooltip
      {...htnPulseTooltipProps({
        net: n,
        state,
        perBeatDispense,
        beats,
        activeEquippedEffects,
        pending,
        avBlockWarning,
      })}
    >
      {trigger}
    </CombatTooltip>
  );
}

export default PlayerHtnPulseTag;
